import type { CryptoPayoutsLoadRequest } from "@src/pages/transactionsOut/classes/CryptoPayoutsCollection"
import type { CryptoPayout } from "@src/pages/transactionsOut/types"
import {
  NetworkCrypto,
  PaginationOrderEnum,
  PayoutResponseDataFeePayerEnum,
  PayoutResponseStatusEnum,
  SymbolCrypto,
} from "@src/shared/api/generated"
import type { CollectionMockResolver } from "@src/shared/classes/Collection/collectionMocks"

export const CRYPTO_PAYOUTS_MOCK_KEY = "crypto-payouts"

const statuses = Object.values(PayoutResponseStatusEnum)
const currencies = [
  {
    address: "0x6EC1868a71F73784c35AD177c6DBA70D22908164",
    network: NetworkCrypto.Ethereum,
    symbol: SymbolCrypto.Usdt,
  },
  {
    address: "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh",
    network: NetworkCrypto.Bitcoin,
    symbol: SymbolCrypto.Btc,
  },
  {
    address: "TJRabPrwbZy45sbavfcjinPJC18kjpRTv8",
    network: NetworkCrypto.Tron,
    symbol: SymbolCrypto.Usdt,
  },
]

const payouts: CryptoPayout[] = Array.from({ length: 24 }, (_, index) => {
  const status = statuses[index % statuses.length]!
  const currency = currencies[index % currencies.length]!
  const isBitcoin = currency.symbol === SymbolCrypto.Btc
  return {
    callback_url:
      index % 3 === 0
        ? undefined
        : "https://merchant.example.com/webhooks/crypto-payouts",
    created_at: new Date(
      Date.UTC(2026, 9, 5, 12) - index * 3600000,
    ).toISOString(),
    data: {
      ...currency,
      aml_level:
        index % 4 === 0 ? undefined : ["low", "medium", "high"][index % 3],
      amount: isBitcoin ? "0.01234567" : "125.500000",
      ext_tx_id:
        status === PayoutResponseStatusEnum.Completed
          ? `0x${String(index + 1).padStart(64, "a")}`
          : undefined,
      fee_payer:
        index % 2 === 0
          ? PayoutResponseDataFeePayerEnum.Merchant
          : PayoutResponseDataFeePayerEnum.Customer,
      fees:
        index % 4 === 0
          ? null
          : {
              fix: {
                amount: isBitcoin ? "0.00010" : "1.0000",
                value: isBitcoin ? "0.00010" : "1.0000",
              },
              percent: {
                amount: isBitcoin ? "0.00005" : "0.6275",
                value: "0.5",
              },
              total_fee: isBitcoin ? "0.00015" : "1.6275",
            },
      from_fiat:
        index % 2 === 0 ? { amount: "150.00", symbol: "USD" } : undefined,
    },
    error:
      status === PayoutResponseStatusEnum.Failed
        ? { code: 1003, message: "AML check failed" }
        : undefined,
    external_id: `MOCK-PAYOUT-${String(index + 1).padStart(3, "0")}`,
    id: `a6ea3200-84c4-4c80-a140-${String(index + 1).padStart(12, "0")}`,
    mass_payout_id:
      index % 2 === 0 ? "c30a512d-27a2-41a1-a53a-289e604310d0" : undefined,
    status,
    type: index % 3 === 0 ? "withdrawal" : "payout",
  }
})

export const getCryptoPayoutsMock: CollectionMockResolver<CryptoPayout> = (
  request,
  { calcOffset, pageSize },
) => {
  const req = (request ?? {}) as CryptoPayoutsLoadRequest
  const order = req.order ?? PaginationOrderEnum.Desc
  const filtered = payouts
    .filter(
      (payout) =>
        (!req.massPayoutId ||
          (req.massPayoutId === "null"
            ? !payout.mass_payout_id
            : payout.mass_payout_id === req.massPayoutId)) &&
        (!req.type || payout.type === req.type) &&
        (!req.extTxId || payout.data.ext_tx_id === req.extTxId) &&
        (!req.status || payout.status === req.status) &&
        (!req.amlLevel ||
          (payout.data.aml_level ?? payout.aml_level) === req.amlLevel) &&
        (!req.network || payout.data.network === req.network) &&
        (!req.symbol || payout.data.symbol === req.symbol) &&
        (!req.address || payout.data.address === req.address) &&
        (!req.externalId || payout.external_id === req.externalId) &&
        (!req.date?.dateFrom || payout.created_at! >= req.date.dateFrom) &&
        (!req.date?.dateTo || payout.created_at! < req.date.dateTo),
    )
    .sort((a, b) =>
      order === "asc"
        ? a.created_at!.localeCompare(b.created_at!)
        : b.created_at!.localeCompare(a.created_at!),
    )

  const offset = calcOffset(req.page)
  return {
    data: filtered.slice(offset, offset + pageSize),
    pagination: {
      limit: pageSize,
      offset,
      order: order as PaginationOrderEnum,
      total: filtered.length,
    },
  }
}
